import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { client } from '@client/graphql/client';
import { graphql } from '@client/graphql/types';

const UPDATE_PUSH_TOKEN = graphql(`
  mutation UpdatePushToken($token: String!) {
    pushNotificationRegisterToken(token: $token)
  }
`);

const REVOKE_PUSH_TOKEN = graphql(`
  mutation RevokePushToken($token: String!) {
    pushNotificationRevokeToken(token: $token)
  }
`);

export class TokenManager {
  private static DEVICE_ID_KEY = 'push_notification_device_id';

  static async getDeviceId(): Promise<string> {
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);

    if (!deviceId) {
      if (Capacitor.isNativePlatform()) {
        const info = await Device.getId();
        deviceId = info.identifier;
      } else {
        deviceId = this.generateWebDeviceId();
      }
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  }

  private static generateWebDeviceId(): string {
    // Create a fingerprint based on browser characteristics
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('device-id', 10, 10);
    const canvasFingerprint = canvas.toDataURL();

    const userAgent = navigator.userAgent;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const deviceFingerprint = `${userAgent}-${timezone}-${canvasFingerprint}`;

    let hash = 0;
    for (let i = 0; i < deviceFingerprint.length; i++) {
      const char = deviceFingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return `web-${Math.abs(hash).toString(36)}`;
  }

  static async registerToken(token: string): Promise<boolean> {
    try {
      await client.mutate({
        mutation: UPDATE_PUSH_TOKEN,
        variables: { token },
      });

      // Store token for later revocation
      localStorage.setItem('push_notification_token', token);

      return true;
    } catch (error) {
      return false;
    }
  }

  static async unregisterToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('push_notification_token');

      if (!token) {
        return false;
      }

      await client.mutate({
        mutation: REVOKE_PUSH_TOKEN,
        variables: { token },
      });

      // Clear stored token
      localStorage.removeItem('push_notification_token');

      return true;
    } catch (error) {
      return false;
    }
  }
}
